import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { forgotPassword, forgotPasswordSubmit } from '@/api/auth';
import { useNotification } from '@/hooks/useNotification';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Spinner } from '@/components/ui/Spinner';

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const { notify } = useNotification();

  // Step 1: email
  const [email, setEmail] = useState('');
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailError, setEmailError] = useState(false);

  // Step 2: code + new password
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyError, setVerifyError] = useState(false);

  async function handleEmailSubmit(e: FormEvent) {
    e.preventDefault();
    if (!email) {
      notify({ id: String(Date.now()), type: 'info', message: 'Form is not complete' });
      return;
    }
    setEmailLoading(true);
    setEmailError(false);
    try {
      await forgotPassword(email);
      setEmailSubmitted(true);
    } catch {
      setEmailError(true);
      notify({ id: String(Date.now()), type: 'error', message: 'Could not perform action' });
    } finally {
      setEmailLoading(false);
    }
  }

  async function handleVerifySubmit(e: FormEvent) {
    e.preventDefault();
    if (!code || !newPassword || !confirmPassword) {
      notify({ id: String(Date.now()), type: 'info', message: 'Form is not complete' });
      return;
    }
    if (newPassword !== confirmPassword) {
      notify({ id: String(Date.now()), type: 'info', message: 'Given passwords do not match' });
      setNewPassword('');
      setConfirmPassword('');
      setCode('');
      return;
    }
    setVerifyLoading(true);
    setVerifyError(false);
    try {
      await forgotPasswordSubmit(email, code, newPassword);
      navigate('/login');
    } catch {
      setVerifyError(true);
      setNewPassword('');
      setConfirmPassword('');
      setCode('');
      notify({ id: String(Date.now()), type: 'error', message: 'Could not perform action' });
    } finally {
      setVerifyLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-md rounded-2xl border border-border/60 bg-card p-8 shadow-xl">
        {!emailSubmitted ? (
          <>
            <h2 className="mb-2 text-xl font-semibold">Forgot password</h2>
            <p className="mb-6 text-sm text-muted-foreground">
              We will send a verification code to your email
            </p>
            <form onSubmit={handleEmailSubmit} className="space-y-4">
              <div>
                <label htmlFor="email" className="mb-1 block text-sm font-medium text-accent-foreground">
                  Email
                </label>
                <Input
                  id="email"
                  type="text"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoFocus
                />
              </div>
              <Button
                type="submit"
                disabled={!email || emailLoading}
                className="w-full"
              >
                {emailLoading && <Spinner size="sm" />}
                {emailError ? 'Try again' : 'Send'}
              </Button>
            </form>
          </>
        ) : (
          <>
            <h2 className="mb-2 text-xl font-semibold">Verify</h2>
            <p className="mb-6 text-sm text-muted-foreground">
              Enter the code from the email and set your new password
            </p>
            <form onSubmit={handleVerifySubmit} className="space-y-4">
              <div>
                <label htmlFor="code" className="mb-1 block text-sm font-medium text-accent-foreground">
                  Code
                </label>
                <Input
                  id="code"
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  autoFocus
                />
              </div>
              <div>
                <label htmlFor="newPw" className="mb-1 block text-sm font-medium text-accent-foreground">
                  New password
                </label>
                <Input
                  id="newPw"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="confirmPw" className="mb-1 block text-sm font-medium text-accent-foreground">
                  Confirm password
                </label>
                <Input
                  id="confirmPw"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </div>
              <Button
                type="submit"
                disabled={!code || !newPassword || !confirmPassword || verifyLoading}
                className="w-full"
              >
                {verifyLoading && <Spinner size="sm" />}
                {verifyError ? 'Try again' : 'Send'}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
